import { useEffect, useMemo, useState } from "react";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AUTH_SOURCE } from "../constants/authSource";
import { useGlobalStore } from "../context/UserContext/userContext";
import { buildCrePatientPath } from "../utils/buildPatientAuthUrl";
import { getCreMsalInstance, initializeCreMsal } from "./creMsalInstance";
import { isCreAccount, syncCreActiveAccount } from "./creAccountUtils";
import { hasMsalRedirectInUrl } from "./creRedirectUtils";
import { getCreLoginIntent, setCreLoginIntent } from "./creLoginIntent";
import CreLoginPage from "./CreLoginPage";
import CreSessionHandler from "./CreSessionHandler";
import CreExtensionGate from "./CreExtensionGate";
import CreExtensionHandoffPage from "./CreExtensionHandoffPage";
import "./cre.css";

function CreEnterpriseGate() {
  const { inProgress, instance, accounts } = useMsal();
  const navigate = useNavigate();
  const authSource = useGlobalStore((s) => s.authSource);

  const creAccounts = useMemo(
    () => accounts.filter(isCreAccount),
    [accounts],
  );

  useEffect(() => {
    // Never overwrite a MedMic extension login that is mid-redirect.
    if (getCreLoginIntent() === "extension") return;
    setCreLoginIntent("enterprise");
  }, []);

  useEffect(() => {
    // Extension-originated sessions must not hit Report Engine landing here.
    if (getCreLoginIntent() === "extension") return;
    if (authSource === AUTH_SOURCE.CRE) {
      const { accessToken, creUser } = useGlobalStore.getState();
      if (accessToken) {
        navigate(buildCrePatientPath(accessToken, creUser), { replace: true });
      }
    }
  }, [authSource, navigate]);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    syncCreActiveAccount(instance);
  }, [instance, accounts, inProgress]);

  if (authSource === AUTH_SOURCE.CRE) {
    return (
      <div className="cre-root cre-loading">
        <div className="cre-spinner" />
        <span>Opening application…</span>
      </div>
    );
  }

  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="cre-root cre-loading">
        <div className="cre-spinner" />
        <span>Authenticating…</span>
      </div>
    );
  }

  if (creAccounts.length === 0) {
    if (hasMsalRedirectInUrl()) {
      return (
        <div className="cre-root cre-loading">
          <div className="cre-spinner" />
          <span>Completing sign-in…</span>
        </div>
      );
    }
    return <CreLoginPage />;
  }

  return <CreSessionHandler />;
}

export default function CreApp() {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeCreMsal()
      .then(() => {
        // Phase 0: MedMic bridge starts only on /cre/extension* (Gate/Handoff),
        // never globally for Enterprise /cre routes.
        setReady(true);
      })
      .catch((err) => {
        console.error("CRE MSAL init failed:", err);
        setError(err?.message || "Failed to initialize authentication");
      });
  }, []);

  if (error) {
    return (
      <div className="cre-root cre-loading">
        <p style={{ color: "var(--cre-danger)", maxWidth: 400, textAlign: "center" }}>
          {error}
        </p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="cre-root cre-loading">
        <div className="cre-spinner" />
      </div>
    );
  }

  return (
    <MsalProvider instance={getCreMsalInstance()}>
      <Routes>
        <Route path="/extension/handoff" element={<CreExtensionHandoffPage />} />
        <Route path="/extension/*" element={<CreExtensionGate />} />
        <Route path="*" element={<CreEnterpriseGate />} />
      </Routes>
    </MsalProvider>
  );
}
